import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  PersonHistory,
  PersonHistoryAttachments,
  PersonHistoryStatusAttachments,
  PersonHistoryStatusTags,
  PersonHistoryTags,
  PersonHistoryTypeAttachments,
} from '../../redux/Candidate/Record/History/History.type';
import CardComponent, { StyledCard } from '../Card.component';
import { DATE_TIME_FORMAT } from '../../constants/date.contant';
import ChevronRightIcon from '../Icons/ChevronRight.icon';
import BadgeComponent, {
  StyledBadge,
  StyledBadgeText,
} from '../Badge.component';
import toTitle from '../../utils/toTitle';
import UserBadgeComponent from '../UserBadge.component';
import { dateFormatter } from '../../utils/formatters';
import AttachmentsHistoryBlock from './AttachmentsHistoryBlock.component';

export interface CandidateRecordHistoryBlockProps {
  info: PersonHistory;
  style?: any;
}

const StyledCandidateRecordHistoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
  font-weight: 500;
`;

const StyledCandidateRecordHistoryAttachments = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCandidateRecordHistoryBodyItemField = styled.div`
  margin: 2px 0 0;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateRecordHistoryBodyItem = styled.div`
  display: grid;
  grid-template-columns: 120px auto;
  gap: 10px;
`;
const StyledCandidateRecordHistoryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${StyledBadge} {
    white-space: break-spaces;
    max-width: 500px;

    ${StyledBadgeText} {
      font-size: 12px;
      line-height: 14px;
      font-weight: 500;
    }
  }
`;
const StyledCandidateRecordHistoryHeaderAction = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateRecordHistoryHeaderRC = styled.div`
  text-align: right;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateRecordHistoryHeaderLC = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  font-weight: 500;
`;
const StyledCandidateRecordHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
export const StyledCandidateRecordHistory = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 10px 40px;

  ${StyledCard} {
    padding: 15px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const capitalizeWord = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const isPersonHistoryAttachments = (
  value: unknown,
): value is PersonHistoryAttachments[] =>
  !!value &&
  Array.isArray(value) &&
  value.every(item => typeof item === 'object' && 'url' in item);

const isPersonHistoryTags = (value: unknown): value is PersonHistoryTags[] =>
  !!value &&
  Array.isArray(value) &&
  value.every(item => typeof item === 'object' && 'name' in item);

const CandidateRecordHistoryBlockComponent = ({
  info,
  style,
}: CandidateRecordHistoryBlockProps) => {
  const { t } = useTranslation();

  const formatText = (isDate: boolean, text: string): string =>
    isDate ? dateFormatter(Number(text), DATE_TIME_FORMAT) : text;

  const renderTagsHistory = (
    status: PersonHistoryStatusTags,
    name: string,
  ): ReactNode => {
    if (status === 'deleted') {
      return (
        <BadgeComponent
          key={name}
          textTransform="lowercase"
          appearance="warning"
          text={name}
        />
      );
    }

    if (status === 'new') {
      return (
        <BadgeComponent
          key={name}
          textTransform="lowercase"
          appearance="success"
          text={name}
        />
      );
    }
  };

  const renderAttachmentsHistory = (
    type: PersonHistoryTypeAttachments,
    url: string,
    status: PersonHistoryStatusAttachments,
  ): ReactNode => {
    if (status === 'CREATED') {
      return (
        <AttachmentsHistoryBlock
          appearance="success"
          type={t(`${capitalizeWord(type)}`)}
          url={url}
        />
      );
    }

    if (status === 'DELETED') {
      return (
        <AttachmentsHistoryBlock
          appearance="warning"
          type={t(`${capitalizeWord(type)}`)}
          url={url}
        />
      );
    }
  };

  return (
    <StyledCandidateRecordHistory style={style}>
      <CardComponent>
        <StyledCandidateRecordHistoryHeader>
          <StyledCandidateRecordHistoryHeaderLC>
            <UserBadgeComponent info={info.author} />
            <StyledCandidateRecordHistoryHeaderAction>
              {t('made changes')}
            </StyledCandidateRecordHistoryHeaderAction>
          </StyledCandidateRecordHistoryHeaderLC>
          <StyledCandidateRecordHistoryHeaderRC>
            {dateFormatter(info.createdAt, DATE_TIME_FORMAT)}
          </StyledCandidateRecordHistoryHeaderRC>
        </StyledCandidateRecordHistoryHeader>
        <StyledCandidateRecordHistoryBody>
          {info.type === 'MERGED' ? (
            <BadgeComponent
              appearance="warning"
              text={t('The employee initiated the merger of two candidates!')}
            />
          ) : (
            Object.keys(info.changes).map((key: string, index) => {
              const isTagHistoryItem = key === 'TAGS';
              const isAttachmentsHistoryItem = key === 'ATTACHMENTS';
              const isDateHistoryItem = key === 'DATE';

              const newHistoryItem = info.changes[key].new_value;
              const oldHistoryItem = info.changes[key].old_value;

              return (
                <StyledCandidateRecordHistoryBodyItem key={info.uuid[index]}>
                  <StyledCandidateRecordHistoryBodyItemField>
                    {t(toTitle(key))}
                  </StyledCandidateRecordHistoryBodyItemField>

                  {isTagHistoryItem && (
                    <StyledCandidateRecordHistoryItem>
                      {isPersonHistoryTags(newHistoryItem) &&
                        newHistoryItem.map(({ name, status }) =>
                          renderTagsHistory(status, name),
                        )}
                    </StyledCandidateRecordHistoryItem>
                  )}

                  {isAttachmentsHistoryItem && (
                    <StyledCandidateRecordHistoryAttachments>
                      {isPersonHistoryAttachments(newHistoryItem) &&
                        newHistoryItem.map(({ url, type, status }) =>
                          renderAttachmentsHistory(type, url, status),
                        )}
                    </StyledCandidateRecordHistoryAttachments>
                  )}

                  {!isAttachmentsHistoryItem && !isTagHistoryItem && (
                    <StyledCandidateRecordHistoryItem>
                      <BadgeComponent
                        textTransform="none"
                        appearance="warning"
                        withTextTranslator={false}
                        text={
                          formatText(isDateHistoryItem, oldHistoryItem) ||
                          t('Not assign')
                        }
                      />
                      <ChevronRightIcon />
                      <BadgeComponent
                        textTransform="none"
                        appearance="success"
                        withTextTranslator={false}
                        text={formatText(
                          isDateHistoryItem,
                          newHistoryItem as string,
                        )}
                      />
                    </StyledCandidateRecordHistoryItem>
                  )}
                </StyledCandidateRecordHistoryBodyItem>
              );
            })
          )}
        </StyledCandidateRecordHistoryBody>
      </CardComponent>
    </StyledCandidateRecordHistory>
  );
};

export default CandidateRecordHistoryBlockComponent;
